import { z } from 'zod';
import { invitationIssuedSchema } from './invitation.js';
import { FEATURES, featureKeySchema, featureSourceSchema } from './features.js';
import { initialTenantAdminResultSchema, initialTenantAdminSchema, normalisedEmailSchema, tenantRegionSchema, } from './tenant.js';
import { paginated, paginationQuerySchema } from './common.js';
/**
 * White-label partners — the organisations that resell OIP under their own
 * brand, each on its own subdomain.
 *
 * Design: `docs/architecture/white-label-partner-tier-design.md`.
 *
 * A partner sits ABOVE the tenant hierarchy and owns no operational data: a
 * brand, a hostname, a set of companies, and a ceiling of features it may pass
 * on. Everything here is PLATFORM-scoped — creating a partner assigns a
 * subdomain and, implicitly, a commercial relationship.
 */
export const WHITE_LABEL_STATUSES = ['ACTIVE', 'SUSPENDED', 'ARCHIVED'];
export const whiteLabelStatusSchema = z.enum(WHITE_LABEL_STATUSES);
export const whiteLabelRoleSchema = z.enum(['WL_ADMIN', 'WL_VIEWER']);
/**
 * A subdomain, not a URL.
 *
 * Deliberately permissive about TLDs and strict about SHAPE, exactly as
 * `sendingDomainNameSchema` is: at least two labels, alphanumerics and hyphens
 * only, no leading or trailing hyphen. A registry-accurate check would reject
 * valid internal hosts and go stale, and it is not what makes this work — DNS
 * pointing at the deployment is.
 *
 * Lower-cased on the way in, because hostnames are case-insensitive and the
 * uniqueness index is on `lower(hostname)`.
 */
export const hostnameSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(4)
    .max(253)
    .regex(/^(?!-)[a-z0-9-]+(?<!-)(\.(?!-)[a-z0-9-]+(?<!-))+$/, 'Must be a hostname such as morgan.oip.com — no scheme, no path, no trailing dot.');
export const whiteLabelPartnerSchema = z.object({
    id: z.string().uuid(),
    /** Stable, human-readable, never reused. Named in support and in audit. */
    code: z.string(),
    legalName: z.string(),
    displayName: z.string(),
    /** Null until a subdomain is assigned; the partner works, unbranded, until then. */
    hostname: z.string().nullable(),
    status: whiteLabelStatusSchema,
    /**
     * The Platform Partner, created with the platform itself.
     *
     * ⚠️ It exists because D0 requires every Company to belong to a Partner, so every
     * customer that came to OIP DIRECTLY is attached to it. It sells nothing, brands
     * nothing and bounds nothing — `ceilingFor`, `brandingLevelOf` and the usability check
     * all treat it as unbounded — and its STATUS may not be changed, because suspending it
     * would refuse every one of those customers at once.
     */
    isSystem: z.boolean(),
    /** Companies beneath it. The number a platform administrator scans for. */
    companyCount: z.number().int(),
    /** People who can administer it. Zero is a partner nobody can reach. */
    adminCount: z.number().int(),
    createdAt: z.string(),
});
export const whiteLabelListQuerySchema = paginationQuerySchema
    .extend({
    // Narrowed to an enum because `orderBy` passes `sort` straight to Prisma as
    // a column name. Every list schema does this; the base leaves it a free
    // string only so each resource can name its own sortable columns.
    sort: z.enum(['displayName', 'code', 'createdAt', 'status']).default('displayName'),
    direction: z.enum(['asc', 'desc']).default('asc'),
    status: whiteLabelStatusSchema.optional(),
    search: z.string().trim().min(1).max(120).optional(),
})
    .strict();
export const whiteLabelListResponseSchema = paginated(whiteLabelPartnerSchema);
/**
 * One entry in the ceiling a partner is created with — a feature, optionally on trial.
 *
 * ⚠️ **A UNION, so a bare key still works.** `features: ['CAN_TELEMETRY']` is the shape this
 * endpoint has always accepted and it is in the published spec; turning it into an array of
 * objects would break every existing caller for the benefit of the ones that want a date.
 * The transform normalises both to one shape, so the handler sees no union at all.
 *
 * ⚠️ **An end date is what MAKES it a trial**, exactly as on the partner's own ceiling card.
 * Absent means the grant stands until somebody reverses it, which is the common case and
 * therefore the one a bare key expresses.
 *
 * ⚠️ **There is no START date, here or anywhere.** No entitlement table on this platform has
 * one — a grant is live from the moment it is written, and `createdAt` is when that was. A
 * deferred start would need a column, both resolvers taught to honour it, and a third row
 * state on every screen: enabled, not yet live, and refused by the server meanwhile.
 */
const ceilingEntrySchema = z
    .union([
    featureKeySchema,
    z
        .object({
        feature: featureKeySchema,
        /** End of the trial. Null is the same as omitting the date: a standing grant. */
        expiresAt: z.string().datetime().nullable(),
    })
        .strict(),
])
    .transform((v) => (typeof v === 'string' ? { feature: v, expiresAt: null } : v));
export const createWhiteLabelRequestSchema = z
    .object({
    code: z
        .string()
        .trim()
        .toLowerCase()
        .min(2)
        .max(40)
        .regex(/^[a-z0-9][a-z0-9-]*[a-z0-9]$/, 'Lower-case letters, digits and hyphens.'),
    legalName: z.string().trim().min(2).max(200),
    displayName: z.string().trim().min(1).max(120),
    /**
     * Invite the initial administrator instead of minting a password for them.
     *
     * ⚠️ **The PARTNER level was the one create path that could not do this**, while
     * companies and fleet accounts could — so a bulk import of partners minted one
     * plaintext password per row and returned it to a report with NO FIELD to carry it.
     * Measured on a real import: the account existed, had a password hash and ZERO
     * invitations, which is an administrator nobody can ever reach. Exactly the failure
     * the deliverability gate exists to prevent, reached past the gate because the gate
     * asks *can we send* and this path then never sent.
     *
     * Optional and defaulting to FALSE, so the platform screen that shows a password once
     * is unchanged — that works, and only a file creating forty is the problem.
     */
    invite: z.boolean().optional(),
    /**
     * Optional at creation. A partner with no hostname is legitimate — the
     * subdomain is usually arranged after the contract — and its users simply
     * see the platform brand until DNS exists.
     */
    hostname: hostnameSchema.optional(),
    /**
     * REQUIRED, and the one field this shares with sub-tenant creation for the
     * same reason: **a partner nobody can administer is unreachable from the
     * moment it exists**, and the only fix is a platform administrator noticing.
     */
    initialAdmin: initialTenantAdminSchema,
    /**
     * The partner's CEILING — what it may pass on to its companies.
     *
     * Optional, and omitting it creates a partner that can grant nothing, which
     * is a legitimate state (provision now, agree terms later) rather than an
     * oversight. It is corrected on the partner's own features card.
     */
    features: z.array(ceilingEntrySchema).optional(),
})
    .strict()
    /*
     * ⚠️ **A MANDATORY feature may not be put on a trial, and this is the sharp edge of the
     * whole idea.** A trial expiry is a date on which the ceiling lowers itself — so a trial
     * on the one feature that may never be lowered is a scheduled instruction to reach the
     * exact state the floor exists to make unreachable: an empty ceiling, refusing every
     * request for every company beneath, with nobody present to connect it to a form somebody
     * filled in weeks earlier.
     *
     * Refused at the EDGE with the feature named, rather than silently dropping the date —
     * a caller who asked for a 30-day trial of telemetry has misunderstood something, and
     * quietly giving them a permanent grant teaches them nothing.
     */
    .superRefine((v, ctx) => {
    for (const entry of v.features ?? []) {
        if (entry.expiresAt !== null && FEATURES[entry.feature].mandatory) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['features'],
                message: `${FEATURES[entry.feature].label} is included in every partner's ceiling and ` +
                    `cannot be put on a trial — the expiry would lower a ceiling that may not be ` +
                    `lowered. Remove the date; it is granted permanently either way.`,
            });
        }
    }
});
export const updateWhiteLabelRequestSchema = z
    .object({
    legalName: z.string().trim().min(2).max(200).optional(),
    displayName: z.string().trim().min(1).max(120).optional(),
    /**
     * An explicit `null` clears the subdomain; omitting the field leaves it
     * alone. Collapsing the two would drop a partner's hostname whenever
     * somebody edited its display name.
     */
    hostname: hostnameSchema.nullable().optional(),
    status: whiteLabelStatusSchema.optional(),
})
    .strict();
/**
 * The create response, carrying the temporary password EXACTLY ONCE.
 *
 * Null when the administrator's identity already existed: an existing account is
 * reused rather than re-credentialled, and issuing a new password for somebody
 * else's account would be a takeover wearing a convenience's clothes.
 */
export const createWhiteLabelResponseSchema = z.object({
    partner: whiteLabelPartnerSchema,
    /**
     * How the invitation went, when one was asked for; null when none was.
     *
     * ⚠️ `NOT_CONFIGURED` is the one that matters — no mail server, so `acceptUrl` comes back
     * and the caller MUST show it, or the partner has an administrator nobody can ever reach.
     * The same field, for the same reason, as `createPartnerCompanyResponseSchema`.
     */
    invitation: invitationIssuedSchema.nullable().default(null),
    admin: z.object({
        userId: z.string().uuid(),
        email: z.string(),
        temporaryPassword: z.string().nullable(),
    }),
});
/** One feature on a partner's ceiling. Mirrors `TenantFeatureDto` deliberately. */
export const whiteLabelFeatureSchema = z.object({
    feature: featureKeySchema,
    enabled: z.boolean(),
    source: featureSourceSchema,
    note: z.string().nullable(),
    expiresAt: z.string().datetime().nullable(),
    updatedAt: z.string().datetime(),
});
export const whiteLabelFeaturesResponseSchema = z.object({
    whiteLabelPartnerId: z.string().uuid(),
    features: z.array(whiteLabelFeatureSchema),
    /**
     * Features this ceiling always holds and which cannot be lowered.
     *
     * ⚠️ Sent rather than derived, for the reason the reseller ceiling already
     * sends its own: the client cannot apply a rule it was never given, and a
     * second copy in the web app is how a screen comes to grey a switch the
     * server would accept — or offer one it refuses. The SERVER is the authority
     * and this is it saying so.
     */
    mandatory: z.array(featureKeySchema),
});
export const updateWhiteLabelFeatureSchema = z
    .object({
    feature: featureKeySchema,
    enabled: z.boolean(),
    source: featureSourceSchema.default('OVERRIDE'),
    note: z.string().trim().max(300).optional(),
    expiresAt: z.string().datetime().nullable().optional(),
})
    .strict();
// ── the partner console ─────────────────────────────────────────────────────
//
// Partner-scoped, not platform-scoped: a partner creates and lists its OWN
// companies. The partner is resolved from the session and never appears in any
// of these shapes (design D15) — accepting it would make every route below a
// way to place a company in somebody else's partner.
/**
 * One RESELLER of a partner — the commercial level between a partner and its companies.
 *
 * ⚠️ **Not to be confused with a FLEET ACCOUNT**, which this console called a "reseller"
 * until the vocabulary was corrected. A Reseller manages companies; a Fleet Account holds
 * vehicles. They sit two levels apart and the word used to name both.
 */
export const partnerResellerSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    /**
     * The Platform Reseller, created with the partner itself.
     *
     * ⚠️ It may be renamed but never withdrawn: `Company.resellerId` is required, so a
     * partner with no reseller can hold no company at all — the bootstrap is what makes the
     * invariant satisfiable rather than a convenience. The console omits the control rather
     * than disabling it, as the Default fleet account's lifecycle buttons already are.
     */
    isSystem: z.boolean(),
    /**
     * ACTIVE | SUSPENDED — and since `20260917000012` this gates every company beneath it,
     * which is what makes the control honest rather than half true.
     */
    status: z.enum(['ACTIVE', 'SUSPENDED']),
    /**
     * How many companies it manages. The number that decides whether it can be withdrawn,
     * so it is returned with the row rather than discovered at the moment one is refused.
     */
    companyCount: z.number().int(),
    createdAt: z.string(),
});
export const partnerResellerListResponseSchema = z.object({
    items: z.array(partnerResellerSchema),
});
/**
 * ⚠️ `status` IS here now, and the journey is the point — it was withheld TWICE for reasons
 * that were correct at the time and each stopped being so.
 *
 * First because `Reseller.status` was read by NOTHING, so a control would have stored a
 * value that changed nothing. Then because `WhiteLabelContextGuard` began refusing a
 * reseller ADMINISTRATOR whose reseller was inactive, which made a control HALF TRUE: it
 * locked the reseller's own people out and left every customer of theirs working, so the
 * screen said suspended and the fleets said otherwise.
 *
 * On 17 September 2026 the cascade was taken as a commercial decision — a suspended
 * reseller's companies are refused, COMPUTED in `TenantContextGuard` rather than written
 * onto any row, which is what makes lifting it immediate. The control is honest now, so
 * the field travels.
 *
 * ⚠️ The SYSTEM reseller is exempt from the cascade and refused the control: every direct
 * customer is attached to it, so suspending it would switch off everybody who came to OIP
 * without a partner.
 */
const resellerNameSchema = z.string().trim().min(2).max(120);
export const createPartnerResellerRequestSchema = z.object({ name: resellerNameSchema }).strict();
export const renamePartnerResellerRequestSchema = z.object({ name: resellerNameSchema }).strict();
export const setPartnerResellerStatusRequestSchema = z
    .object({ status: z.enum(['ACTIVE', 'SUSPENDED']) })
    .strict();
/**
 * What a reseller may sell.
 *
 * ⚠️ **`restricted` is the load-bearing field, not `features`.** An empty list means NO
 * RESTRICTION — the reseller inherits its partner's whole ceiling — and a screen given only
 * the array cannot tell that from "permitted nothing", which is the opposite fact. The same
 * distinction `samplesRead` draws between a sensor that is absent and a device that stopped.
 */
export const partnerResellerFeaturesResponseSchema = z.object({
    restricted: z.boolean(),
    features: z.array(z.string()),
    /**
     * Which of `features` the reseller has by FLOOR rather than by the partner's choice.
     *
     * ⚠️ Stated rather than left for a screen to infer — the `source: 'IMPLIED'` argument.
     * A tick nobody chose would otherwise read as a decision somebody made, and a control
     * that cannot be changed would be discovered by pressing it. Empty while the reseller
     * is UNRESTRICTED, because it then inherits the whole partner ceiling and there is no
     * allow-list for a floor to sit in.
     */
    mandatory: z.array(z.string()),
    /**
     * The partner's own ceiling, so a screen can offer what is actually available rather
     * than every key in the catalogue — the rule the vehicle picker follows by listing what
     * REPORTED rather than what exists.
     */
    partnerCeiling: z.array(z.string()),
});
export const setPartnerResellerFeatureRequestSchema = z
    .object({
    feature: z.string().trim().min(1).max(64),
    /**
     * ⚠️ `false` REMOVES the entry from the allow-list rather than storing a disabled row:
     * under an allow-list, disabled and absent mean the same thing. Removing the LAST entry
     * is refused (409) — an empty list means "no restriction", so it would widen rather
     * than narrow, which is the opposite of what pressing it implies.
     */
    enabled: z.boolean(),
})
    .strict();
/**
 * Move a company to a different reseller of the SAME partner.
 *
 * ⚠️ A governance action rather than an edit: which reseller manages a company decides who
 * bills and supports it, so it is its own route with its own audit row rather than a field
 * on a company PATCH. The target must belong to the caller's partner — enforced by the
 * composite key `(whiteLabelPartnerId, resellerId) → Reseller(partnerId, id)`, so the
 * database refuses a cross-partner move even if the service forgets.
 */
export const moveCompanyResellerRequestSchema = z
    .object({ resellerId: z.string().uuid() })
    .strict();
/** One company of a partner, as its own administrator sees it. */
export const partnerCompanySchema = z.object({
    tenantId: z.string().uuid(),
    name: z.string(),
    region: z.string(),
    status: z.string(),
    /**
     * Which reseller manages it.
     *
     * ⚠️ NULLABLE, and that is not defensive: `Company.resellerId` is required by a CHECK,
     * but this same item schema is served to a PLATFORM administrator, and the level is a
     * fact about the partner agreement rather than about the company. A row whose reseller
     * cannot be resolved renders as unassigned rather than inventing a name — the
     * `policyLabel` rule, one level up.
     */
    resellerId: z.string().uuid().nullable(),
    resellerName: z.string().nullable(),
    /**
     * Fleet accounts beneath it, which is the number a partner scans for — a company
     * with none is one that has not been set up yet.
     */
    fleetAccountCount: z.number().int(),
    vehicleCount: z.number().int(),
    /**
     * The fleet accounts beneath it, so the console can show the SHAPE of what a
     * partner runs rather than two numbers.
     *
     * Returned with the company rather than fetched per row: the query that
     * attributes a vehicle to its company already reads these, and a partner has
     * tens of fleet accounts, not thousands. VEHICLES are deliberately not included —
     * see the note on the tree.
     */
    fleetAccounts: z.array(z.object({
        tenantId: z.string().uuid(),
        name: z.string(),
        status: z.string(),
        /** The one that holds the fleet by construction and cannot be suspended. */
        isDefault: z.boolean(),
        vehicleCount: z.number().int(),
    })),
    createdAt: z.string(),
});
export const partnerCompanyListResponseSchema = z.object({
    items: z.array(partnerCompanySchema),
    /**
     * The cap from the partner row — a blast radius, not a licence, exactly as
     * `maxSubTenants` bounds fleet accounts per company. Returned with the list so the
     * console can say "8 of 50" rather than discovering the bound at the moment a
     * create is refused.
     */
    maxCompanies: z.number().int(),
});
/**
 * The same list, read by a PLATFORM administrator about one partner.
 *
 * ⚠️ Deliberately the SAME item schema rather than a platform-specific one. A
 * company is a company; two shapes for one thing is how the counts on the
 * platform's screen and the counts on the partner's own console start to
 * disagree, and the whole point of this route is that they cannot.
 *
 * The platform screen showed `Companies 3` as a stat with nothing behind it —
 * a number a reader could not produce, which is the `policyLabel` complaint
 * ("nobody can retune a UUID") in its other form: a fact stated and not
 * reachable.
 */
export const platformPartnerCompaniesResponseSchema = partnerCompanyListResponseSchema;
/**
 * `initialAdmin` is REQUIRED, exactly as it is for a sub-tenant (D18) and for a
 * partner itself: a company nobody can administer is unreachable from the moment
 * it exists.
 *
 * There is deliberately **no `features` field**. A partner must not be able to
 * grant entitlements by creating a company, or the ceiling is a formality — the
 * company starts with nothing and the partner grants afterwards, where the grant
 * is a separate, audited decision (D15).
 *
 * There is also no `whiteLabelPartnerId`. It comes from the session.
 */
export const createPartnerCompanyRequestSchema = z
    .object({
    /**
     * Send the initial administrator an INVITATION instead of minting a password.
     *
     * ⚠️ **Defaults to FALSE, so nothing existing changes.** Flipping the default
     * changes what every create screen does and what every caller gets back — a
     * decision to take deliberately rather than as a side effect. The bulk import
     * will always pass `true`: it is the only way a fifty-row file does not
     * produce fifty unrecoverable secrets.
     */
    /*
     * ⚠️ `.optional()` rather than `.default(false)`, and that is the `z.input`
     * vs `z.infer` trap this codebase already records for
     * `CopilotAskRequestInput`: a `.default()` is PRESENT and REQUIRED in the
     * parsed type, so every existing caller — including the create-company
     * dialog — would have to state `invite: false` to say nothing had changed.
     * Absent already means not invited.
     */
    invite: z.boolean().optional(),
    name: z.string().trim().min(2).max(120),
    region: tenantRegionSchema.default('US'),
    /**
     * What to call the company's first fleet account.
     *
     * Every company gets one at creation — it is the tenant that owns the fleet,
     * so a company without it holds nothing. Naming it here means it is never a
     * phantom (D15): left unset it is called "Default", which is what every
     * migrated customer's is called and is honest rather than pretending the
     * partner chose it.
     */
    firstFleetAccountName: z.string().trim().min(2).max(120).optional(),
    /**
     * Which policy template the new fleet account starts from.
     *
     * ⚠️ **OMITTED means the DEFAULT template; explicit `null` means NONE.** Collapsing those
     * two would make "I did not choose" and "I chose to configure this myself" the same
     * request, and one of them must not silently install 24 rules somebody deliberately
     * declined. The same distinction the AI allowance already draws between an absent field
     * and an explicit null.
     *
     * ⚠️ Why a default exists at all: a company created with NO policies is the "quietly
     * inert" state — events correlate, incidents open, risk is scored and **nothing is ever
     * recommended**, which reads as the intelligence engine being broken. That has shipped
     * four times. Starting from a template is the safe default and declining is the
     * deliberate act, not the other way round.
     */
    policyTemplateId: z.string().uuid().nullable().optional(),
    /**
     * Which reseller manages the new company.
     *
     * ⚠️ OPTIONAL, defaulting to the partner's Platform Reseller — which is exactly what
     * every company created before this field existed got, so the omitted case is not a
     * fallback but the established behaviour. Requiring it would mean a partner that has
     * never created a reseller could no longer create a company.
     */
    resellerId: z.string().uuid().optional(),
    /**
     * Create a NEW reseller and file this company under it, in one act.
     *
     * ⚠️ Signing a customer under a brand-new reseller was two screens; the reseller and
     * the company are now created in ONE TRANSACTION, because a reseller created by a
     * request that then fails is an orphan nobody asked for, and a company created first
     * has nowhere valid to hang (D0).
     *
     * ⚠️ **Refused alongside `resellerId`.** Two ways to say where a company lands is two
     * answers to one question, and whichever lost would lose SILENTLY.
     *
     * ⚠️ A RESELLER-scoped caller may send neither: its own comes from the session (D15),
     * and creating a sibling would be filing a customer outside its own book. Enforced in
     * the service, where the caller's level is known.
     */
    newResellerName: z.string().trim().min(2).max(120).optional(),
    initialAdmin: initialTenantAdminSchema,
})
    .strict()
    .refine((v) => !(v.resellerId && v.newResellerName), {
    message: 'Name a reseller or create one, not both.',
    path: ['newResellerName'],
});
/**
 * A policy template a company can be created from.
 *
 * ⚠️ Only templates that would actually copy something are listed. One holding no policy with
 * an active version is a choice that silently does nothing — the "control that lies" defect
 * this product keeps recording, in a dropdown.
 */
export const policyTemplateSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    isDefault: z.boolean(),
    /** How many policies it would copy. Shown so the choice is not taken on trust. */
    policyCount: z.number().int().nonnegative(),
    /**
     * The rules themselves, so the choice can be READ rather than taken on the count.
     *
     * ⚠️ **In the SAME response as `policyCount`, deliberately**, though the codebase's rule is
     * that a collapsed disclosure fetches nothing. Two reads of "what is in this template"
     * could disagree — and the count overstating what the copy delivers is the exact defect
     * this feature already shipped once. One computation, one answer, and the list is a few
     * dozen short strings rather than a page of data.
     *
     * ⚠️ Holds exactly what would be COPIED, so a policy the copy would skip is absent here
     * too. A list a reader checks against the resulting company must not name a rule that
     * never arrives.
     */
    policies: z.array(z.object({
        name: z.string(),
        /** The canonical event type it judges. A string on read, as everywhere else. */
        eventType: z.string(),
    })),
    /**
     * Canonical event types this template does NOT cover.
     *
     * ⚠️ Excludes the types that deliberately raise nothing, or every template would report two
     * permanent holes and the warning would be ignored.
     */
    missingEventTypes: z.array(z.string()),
});
export const policyTemplateListResponseSchema = z.object({
    items: z.array(policyTemplateSchema),
});
export const createPartnerCompanyResponseSchema = partnerCompanySchema.extend({
    /**
     * How the invitation went, when one was asked for; null when none was.
     *
     * ⚠️ `NOT_CONFIGURED` is the one that matters — no mail server, so `acceptUrl`
     * comes back and the screen MUST show it, or the company has an administrator
     * nobody can ever reach.
     */
    invitation: invitationIssuedSchema.nullable().default(null),
    initialAdmin: initialTenantAdminResultSchema,
    /**
     * How many decision policies the chosen template actually copied.
     *
     * ⚠️ On the RESPONSE rather than on `partnerCompanySchema`, because it is a fact about the
     * act of creation and not about the company — a list row carrying it would be answering a
     * question nobody asked of every other row on the screen.
     *
     * ⚠️ **Zero is the value worth reporting.** "I ran the step" and "the step did something"
     * are different claims, and the gap between them is the quietly-inert state this whole
     * mechanism exists to prevent — a company that correlates events, scores risk and
     * recommends nothing, which reads as the intelligence engine being broken. It has shipped
     * four times. Saying the number at the moment of creation is what closes that loop.
     */
    policiesCopied: z.number().int().nonnegative(),
});
/** What a partner administrator is told about the partner they administer. */
export const partnerContextResponseSchema = z.object({
    whiteLabelPartnerId: z.string().uuid(),
    code: z.string(),
    displayName: z.string(),
    maxCompanies: z.number().int(),
    companyCount: z.number().int(),
    /**
     * The ceiling, as KEYS rather than rows.
     *
     * A partner may see what it is permitted to sell; it may not see the grant's
     * source, note or expiry, which are terms of ITS contract with the platform
     * and belong on the platform screen. Closed under implications, so it is what
     * the partner can actually pass on rather than what was typed.
     */
    features: z.array(featureKeySchema),
    /**
     * The RESELLER this caller administers, or null for the whole partner.
     *
     * ⚠️ Every field above means something different at the two levels, and the screen has
     * to be able to tell: `features` is what the CALLER may sell (already narrowed for a
     * reseller), while `maxCompanies` is the PARTNER's allowance and a reseller cannot spend
     * it at all. Shown to one, "1 of 50 companies" reads as licence to create forty-nine.
     */
    resellerId: z.string().uuid().nullable(),
    resellerName: z.string().nullable(),
});
// ── the partner's people ────────────────────────────────────────────────────
/**
 * Somebody who administers or observes a partner.
 *
 * Carries the GLOBAL account status alongside the membership's, which the tenant
 * member DTO deliberately does not — `AdminMemberDto` shows membership status
 * only, so a member page cannot say whether somebody can actually sign in. That
 * is a known gap there and there is no reason to reproduce it here: a partner
 * with one administrator whose account is deactivated has, in effect, none, and
 * the count would say one.
 */
export const partnerMemberSchema = z.object({
    userId: z.string().uuid(),
    email: z.string(),
    displayName: z.string(),
    role: whiteLabelRoleSchema,
    /** The MEMBERSHIP. Deactivating it leaves the person's account alone. */
    isActive: z.boolean(),
    /** The ACCOUNT. `INACTIVE` means they cannot sign in at all, anywhere. */
    accountStatus: z.string(),
    addedAt: z.string(),
    /**
     * Which level this membership is at — null is the whole partner.
     *
     * ⚠️ Returned so the members list can SAY so. Without it two rows reading "Administrator"
     * mean materially different things — one reaches every company of the partner, the other
     * one reseller's — and the screen cannot tell them apart.
     */
    resellerId: z.string().uuid().nullable(),
    resellerName: z.string().nullable(),
});
export const partnerMemberListResponseSchema = z.object({
    items: z.array(partnerMemberSchema),
    /**
     * People who can actually administer this partner right now: an ACTIVE
     * WL_ADMIN membership held by an ACTIVE account.
     *
     * Computed rather than counted from `items` by the client, because the rule
     * that protects the last one is the server's and two definitions of "can
     * administer" would eventually disagree — the screen would offer a removal
     * the API then refuses.
     */
    adminCount: z.number().int(),
});
export const addPartnerMemberRequestSchema = z
    .object({
    email: normalisedEmailSchema,
    /** Used only when the account does not already exist. */
    displayName: z.string().trim().min(1).max(120).optional(),
    /**
     * REQUIRED and explicit. There is no default: `WL_ADMIN` can create
     * companies and edit the brand, `WL_VIEWER` derives nothing at all, and a
     * silent default would be wrong half the time in whichever direction it
     * was chosen.
     */
    role: whiteLabelRoleSchema,
    /**
     * The RESELLER this person administers, or null/absent for the whole partner.
     *
     * ⚠️ **Without it the Reseller level is enforced and unreachable.** The membership
     * column, the guard, the scoped routes, the ceiling and the console all shipped before
     * this did — so appointing somebody to administer a reseller was possible only by a
     * direct database insert, which makes the level a thing the product describes and
     * cannot do. The `20260917000007` shape, one level along.
     *
     * Re-appointing MOVES the level: somebody already holding a membership under this
     * partner is updated rather than given a second one, because a second administering
     * membership does not add access — it removes it, by reaching the guard's
     * refuse-to-guess branch.
     */
    resellerId: z.string().uuid().nullable().optional(),
})
    .strict();
export const addPartnerMemberResponseSchema = z.object({
    member: partnerMemberSchema,
    /**
     * Returned EXACTLY ONCE, and only when the account was newly created. Null
     * when an existing identity was reused — issuing a password for somebody
     * else's account would be a takeover wearing a convenience's clothes.
     */
    temporaryPassword: z.string().nullable(),
});
export const updatePartnerMemberRequestSchema = z
    .object({
    role: whiteLabelRoleSchema.optional(),
    /**
     * Deactivating leaves the ROW, so restoring reuses it and `addedAt`
     * survives — the same rule tenant memberships follow (D2). Nothing here is
     * hard-deleted.
     */
    isActive: z.boolean().optional(),
})
    .strict()
    .refine((v) => Object.keys(v).length > 0, { message: 'At least one field must be provided' });
//# sourceMappingURL=white-label.js.map